import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';

// Shared-rig contract (see the framing addendum). 'forbidden': the only light
// in this scene is the fire, and it is computed from a temperature field. A
// rig's wash cones would be a second, contradictory light source.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'ember',
  family: 'organic',
  danceability: 0.15,
  requiresTier: 1,
  requiresFloatTextures: true,
  energyLevel: 0.20,
  bpmMin: 40,
  bpmMax: 135,
  movement: 0.25,
  tonality: 0.85,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.55] },
  enabled: FLOAT_TEXTURES_OK
};

// Ember: a bed of embers seen close up. Folk, blues, jazz, warm ambient,
// winter music.
//
// THE ONE IDEA THIS FILE IS BUILT ON: a fire does not get COLOURED, it
// RADIATES. Every colour on screen is computed from a temperature, through a
// blackbody curve, and nothing else. That is what makes the image read as fire
// within a frame rather than as orange blobs - the black -> dark red -> orange
// -> yellow -> white progression is the thing the eye recognises, and it only
// comes out right if the ramp is a function of temperature.
//
// It is also why the cover's palette never touches the fire. On a blue cover
// the embers stay orange. That is a deliberate, stated cost: a blue fire does
// not read as fire, and here credibility beats chromatic coherence. The palette
// tints the BACKGROUND, faintly, and stops there.
//
// THREE PASSES PER FRAME, all driven from the display quad's onBeforeRender:
//   1. combustion, on a small RG float grid, at a FIXED timestep
//   2. the scene - background, bed, sparks - into an offscreen target
//   3. hot-air refraction, full resolution, sampling that target
//
// Pass 3 is the one that decides whether this looks like heat or like a
// shader. Without it the picture is orange dots on black; with it the air above
// the bed moves and the whole thing settles into being a fire. It is also the
// dominant cost - one dependent texture read plus noise per pixel - which is
// why tier 1 gets a cheaper version of it rather than losing it.

// --- the simulation grid ---------------------------------------------------
// FIXED timestep, and this is not a style preference: a combustion loop with a
// t^4 term diverges spectacularly on a variable dt. Two substeps maximum so a
// long frame cannot cascade into a spiral of catch-up work.
const SIM_DT = 1 / 60;
const SIM_MAX_SUBSTEPS = 2;

const BURN_RATE = 1.35;        // how fast fuel goes when it is hot
const HEAT_PER_FUEL = 1.55;
// Diffusion is what makes heat rise here - the up/down asymmetry in the
// stencil IS the buoyancy - but it is also an averaging filter, and the
// stencil weights sum to 0.89, so the value below is very nearly "fraction of
// the way to the neighbourhood average, per step". At 60 steps a second, 0.42
// erased any spatial structure in about five frames: measured, the bed sat at
// p05 0.85 against a median 1.12, one flat orange sheet. Embers need the
// opposite - large nearly-out areas with a few near-white cells in them - so
// this has to stay low enough that combustion can outrun it.
const DIFFUSION = 0.075;
const COOLING_MIN = 0.055;     // mid-track: the fire is at its liveliest
const COOLING_MAX = 0.115;     // the two ends of a track
const FUEL_REGEN = 0.030;      // per second, into wandering patches (see the sim shader)
const AMBIENT_SEED = 0.055;    // the floor the bed never drops below

// --- the bed ---------------------------------------------------------------
// The bed occupies the bottom third; the cover sits in the middle of the
// frame, so there is little conflict and the safe zone is applied to the
// sparks alone (they are the only thing that crosses it).
const BED_TOP = 0.36;          // in frame height, measured from the bottom
const SAFE_FLOOR = 0.5;

// --- sparks ----------------------------------------------------------------
// A spark is tiny. 1.5 to 3 px and never more - the moment they are bigger
// they read as embers floating in the air, which is a different and much less
// convincing thing.
const SPARK_PX_MIN = 1.5;
const SPARK_PX_MAX = 3.0;
const SPARK_LIFE_MIN = 1.5;
const SPARK_LIFE_MAX = 4.0;
const SPARK_RISE = 0.30;       // terminal rise speed, frame heights per second
const SPARK_RISE_TAU = 0.85;   // how fast it reaches that speed - drag
const SPARK_DRIFT = 0.055;
const SPARK_COOL_TAU = 1.25;   // sparks must go out BEFORE the top of the frame

// --- air refraction --------------------------------------------------------
// 3 to 6 px maximum. Past that it stops reading as heat and starts reading as
// an effect, which is the whole failure mode this layer has.
const WARP_PX_MAX = 5.0;
const WARP_SCROLL = 0.22;      // the field drifts UPWARD, like the air does

// --- reactivity ------------------------------------------------------------
// No pulse anywhere in this file. A fire does not pulse in time.
const REVIVE_RISE = 0.12;      // a rise in bands.low pokes the fire
const REVIVE_HEAT = 1.25;
const REVIVE_FUEL = 0.85;
const REVIVE_RADIUS = 0.085;
const UPDRAFT_MIN = 0.75;
const UPDRAFT_MAX = 1.55;
// Sparks per second, from bands.high. These are low on purpose: the live
// population is rate x lifetime, so 90-340/s against a 2.75s average life
// meant 250-935 sparks alive at once in a 600 pool - permanently saturated,
// and it read as falling confetti rather than as a fire throwing the odd
// spark. 12-55/s keeps 30-150 in the air, which is what a real bed does.
const BIRTH_RATE_MIN = 12;
const BIRTH_RATE_MAX = 55;

const TIER = {
  0: { simW: 192, simH: 96, sparks: 400, warp: 'simple', ash: false, cracks: true },
  1: { simW: 192, simH: 96, sparks: 400, warp: 'simple', ash: false, cracks: true },
  2: { simW: 256, simH: 128, sparks: 600, warp: 'full', ash: true, cracks: true }
};
const DEFAULT_TIER = 2;

const PROBE_W = 32;            // CPU-side heat readback, for spark birthplaces
const PROBE_H = 16;
const PROBE_EVERY_MS = 220;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(n) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(n);
}

// Shared by the bed and the sparks. Written once, because the whole file
// depends on both using the SAME curve - a spark is a small piece of the bed
// that left, and if its ramp differed the two would read as separate effects.
//
// The three channels switch on at staggered temperatures, which is what
// produces black -> dark red -> orange -> yellow -> white as one continuous
// progression rather than a set of steps. The final term takes it cleanly to
// black at the bottom instead of leaving a dim red floor everywhere.
export const BLACKBODY_CHUNK = `
  vec3 blackbody(float t) {
    vec3 c;
    c.r = smoothstep(0.02, 0.28, t);
    c.g = smoothstep(0.22, 0.72, t);
    c.b = smoothstep(0.58, 1.00, t);
    return c * smoothstep(0.0, 0.10, t);
  }
`;

const NOISE_CHUNK = `
  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x),
               mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 4; i++) { v += a * vnoise(p); p *= 2.02; a *= 0.5; }
    return v;
  }
`;

const simVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// highp on the simulation, and it has to be: the t^4 cooling term squares
// twice, so a temperature of 2 becomes 16 before it is scaled back down, and
// on a GPU where mediump is genuinely fp16 (the TV) the intermediate range
// matters even though the values themselves are small.
const simFragmentShader = `
  precision highp float;

  uniform sampler2D uPrev;
  uniform vec2 uTexel;
  uniform float uDt;
  uniform float uBurnRate;
  uniform float uHeatPerFuel;
  uniform float uDiffusion;
  uniform float uCooling;
  uniform float uFuelRegen;
  uniform vec3 uRevive;        // xy centre, z strength (0 = nothing this step)
  uniform float uReviveRadius;
  uniform float uTime;

  varying vec2 vUv;

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x),
               mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  void main() {
    vec2 st = texture2D(uPrev, vUv).rg;
    float temp = st.r;
    float fuel = st.g;

    // Combustion. Fuel goes faster where it is already hot, which is the
    // feedback that makes a bed of embers behave like one: a hot patch eats
    // its own fuel and burns out, while its neighbours warm up and take over.
    float burn = min(fuel, temp * uBurnRate * uDt);
    fuel -= burn;
    temp += burn * uHeatPerFuel;

    // Anisotropic diffusion - more upward than downward. This is the whole
    // reason heat rises here without a single line of convection: the
    // asymmetry in the stencil IS the buoyancy.
    float up    = texture2D(uPrev, vUv + vec2(0.0,  uTexel.y)).r;
    float down  = texture2D(uPrev, vUv - vec2(0.0,  uTexel.y)).r;
    float left  = texture2D(uPrev, vUv - vec2(uTexel.x, 0.0)).r;
    float right = texture2D(uPrev, vUv + vec2(uTexel.x, 0.0)).r;
    float diff = (up * 0.35 + down * 0.10 + left * 0.22 + right * 0.22) - temp * 0.89;
    temp += diff * uDiffusion;

    // Radiative cooling, fourth power, as a real body radiates. This is what
    // gives the decay its character and a linear term cannot: a very hot ember
    // dumps its heat fast, a warm one lingers for a long time. Linear cooling
    // makes every ember fade at the same rate, which reads as flat and wrong.
    temp -= uCooling * temp * temp * temp * temp * uDt;
    temp = max(temp, 0.0);

    // Fuel creeps back - but in slowly WANDERING PATCHES, not evenly, and
    // this is what decides whether the bed reads as embers at all. With a
    // uniform trickle every cell reaches the same burn/cool equilibrium and
    // the whole bed sits at one temperature: measured, p10 0.85 against a
    // median of 1.12, a flat orange sheet that reads as lava. Embers are
    // CONTRASTY - a few cells near white next to large areas nearly out - and
    // that contrast has to be fed by where the fuel is.
    //
    // The patch field drifts very slowly, so islands die back and relight
    // somewhere else over tens of seconds rather than staying put.
    // (named fuelPatch, not patch: 'patch' is a reserved word in GLSL ES)
    float fuelPatch = vnoise(vec2(vUv.x * 4.5, vUv.y * 2.4 + uTime * 0.021));
    fuelPatch *= vnoise(vec2(vUv.x * 9.0 - uTime * 0.013, vUv.y * 4.5));
    fuelPatch = smoothstep(0.20, 0.62, fuelPatch);
    // Still biased to the edges on top of that, so the middle - the hottest
    // part - is the part that genuinely burns itself out.
    float edge = smoothstep(0.34, 0.0, vUv.x) + smoothstep(0.66, 1.0, vUv.x);
    fuel = min(1.0, fuel + uFuelRegen * uDt * fuelPatch * (0.35 + edge));

    // A rise in bands.low: someone poked the fire. Heat AND fuel, in one spot.
    if (uRevive.z > 0.0) {
      vec2 d = (vUv - uRevive.xy) * vec2(2.0, 1.0);
      float g = exp(-dot(d, d) / max(uReviveRadius * uReviveRadius, 1e-5));
      temp += uRevive.z * g;
      fuel = min(1.4, fuel + uRevive.z * g * 0.7);
    }

    gl_FragColor = vec4(temp, fuel, 0.0, 1.0);
  }
`;

// Downsamples the temperature field into a tiny RGBA8 target so the CPU can
// read it back cheaply. Reading the float grid directly would be 128KB and a
// pipeline stall every time; this is 2KB, a few times a second.
const probeFragmentShader = `
  precision mediump float;
  uniform sampler2D uSim;
  varying vec2 vUv;
  void main() {
    float t = texture2D(uSim, vUv).r;
    gl_FragColor = vec4(clamp(t * 0.5, 0.0, 1.0), 0.0, 0.0, 1.0);
  }
`;

const fullscreenVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// Background + bed, in one pass. They share the pass because the bed lights
// the background from below and the two need the same temperature lookup.
function sceneFragmentShader(opts) {
  return `
  precision mediump float;

  uniform sampler2D uSim;
  uniform vec3 uTint;
  uniform float uTime;
  uniform float uAspect;
  uniform float uDebug;       // 1 = temperature, 2 = fuel
  // Maps simulation temperature onto the blackbody curve's 0-1 domain (about
  // 800K to 2600K).
  //
  // The exponent is the part that matters, and it is there because of the
  // physics rather than in spite of it. Radiative cooling balances heat input
  // at t^4 = input/cooling, so temperature goes as the FOURTH ROOT of how much
  // fuel a cell is getting: a tenfold difference in fuel is a 1.8x difference
  // in temperature. Measured on the running bed, that puts p05 at 0.92 and the
  // median at 1.27 - a genuinely narrow field, and fed straight into the ramp
  // it lands as one flat orange sheet no matter how patchy the fuel is made.
  // So the compression the t^4 law imposes is undone here, on the way into the
  // curve. It stays a monotonic function of temperature, so the progression is
  // still continuous and still driven by heat alone.
  uniform float uTempScale;
  uniform float uTempGamma;
  varying vec2 vUv;

  ${BLACKBODY_CHUNK}
  ${NOISE_CHUNK}

  // Voronoi, for the cracks between lumps of charcoal. A uniform glowing
  // surface reads as lava; charcoal is BROKEN, and the dark cold seams between
  // the pieces are most of what says so.
  vec2 voronoi(vec2 p) {
    vec2 n = floor(p), f = fract(p);
    float d1 = 8.0, d2 = 8.0;
    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 g = vec2(float(i), float(j));
        vec2 o = vec2(hash12(n + g), hash12(n + g + 17.3));
        float d = length(g + o - f);
        if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) { d2 = d; }
      }
    }
    return vec2(d1, d2);
  }

  // Temperature at a frame position, or 0 above the bed.
  float bedTemp(vec2 uv) {
    if (uv.y > ${BED_TOP.toFixed(3)}) return 0.0;
    return texture2D(uSim, vec2(uv.x, uv.y / ${BED_TOP.toFixed(3)})).r;
  }

  void main() {
    vec2 uv = vUv;

    if (uDebug > 0.5) {
      vec2 s = texture2D(uSim, vec2(uv.x, clamp(uv.y / ${BED_TOP.toFixed(3)}, 0.0, 1.0))).rg;
      float v = uDebug < 1.5 ? s.r * 0.5 : s.g * 0.7;
      gl_FragColor = vec4(vec3(uv.y <= ${BED_TOP.toFixed(3)} ? v : 0.0), 1.0);
      return;
    }

    // --- background ---------------------------------------------------------
    // It has to have STRUCTURE, otherwise the refraction pass has nothing to
    // bend and does nothing visible. A flat black would waste the whole layer.
    float glow = smoothstep(0.85, 0.0, uv.y);
    vec3 bg = uTint * (0.018 + 0.075 * glow * glow);
    bg += vec3(0.055, 0.020, 0.006) * glow * glow * 1.4;
    // Suggested dark shapes around the edge, lit from underneath by the fire -
    // this is what gives the picture a PLACE rather than a void.
    float shapes = fbm(vec2(uv.x * 3.1, uv.y * 2.2 + 0.4));
    float rim = smoothstep(0.52, 0.78, shapes) * smoothstep(0.15, 0.55, uv.y);
    bg = mix(bg, bg * 0.35 + vec3(0.050, 0.017, 0.004) * glow, rim * 0.8);
    bg += vec3(0.010, 0.008, 0.012) * fbm(vec2(uv.x * 7.0, uv.y * 5.0));

    vec3 col = bg;

    // --- the bed ------------------------------------------------------------
    if (uv.y <= ${BED_TOP.toFixed(3)}) {
      vec2 bedUv = vec2(uv.x, uv.y / ${BED_TOP.toFixed(3)});
      float t = texture2D(uSim, bedUv).r;

      ${opts.cracks ? `
      // Cracks. Multiplicative, and they also cool the surface they sit on:
      // a seam is both darker and genuinely less hot than the lump beside it.
      vec2 vo = voronoi(vec2(bedUv.x * uAspect * 26.0, bedUv.y * 13.0));
      float seam = smoothstep(0.0, 0.085, vo.y - vo.x);
      t *= mix(0.55, 1.0, seam);
      ` : ''}

      // A little relief, shaded by the fire's own light from below - the bed
      // is not a flat plane.
      float h = fbm(vec2(bedUv.x * uAspect * 14.0, bedUv.y * 7.0));
      float hx = fbm(vec2(bedUv.x * uAspect * 14.0 + 0.6, bedUv.y * 7.0));
      float hy = fbm(vec2(bedUv.x * uAspect * 14.0, bedUv.y * 7.0 + 0.6));
      float shade = 1.0 + (hy - h) * 1.1 - (hx - h) * 0.35;
      t *= clamp(shade, 0.55, 1.45);

      float tn = pow(clamp(t * uTempScale, 0.0, 1.0), uTempGamma);
      vec3 fire = blackbody(tn);
      // Intensity climbs FASTER than the colour does, so the hottest embers
      // genuinely blow out and bloom while the dim ones stay dim. A linear
      // intensity would give a uniformly lit bed with a colour gradient on it,
      // which is not what a fire looks like.
      float energy = pow(tn, 1.55) * 2.35;
      vec3 bedCol = fire * energy;

      ${opts.ash ? `
      // Grey ash over the coldest parts, itself cracked so the red shows
      // through underneath.
      // Ash sits only on cells that are genuinely nearly out, and it is a
      // DARK grey - the first pass used 0.085 over most of the bed and read as
      // a milky veil laid over the fire rather than as ash lying in it.
      float ashMask = smoothstep(0.16, 0.02, tn);
      float ashCrack = smoothstep(0.34, 0.62, fbm(vec2(bedUv.x * uAspect * 30.0, bedUv.y * 15.0)));
      vec3 ash = vec3(0.030, 0.028, 0.027) * (0.35 + 0.65 * ashCrack);
      bedCol = mix(bedCol, ash + bedCol * 0.55, ashMask * ashCrack * 0.6);
      ` : ''}

      // The bed meets the background through its own falloff, never a hard line.
      col = mix(col, col + bedCol, smoothstep(1.0, 0.86, bedUv.y));
    }

    // Dither. Mandatory: these are long, very dark gradients and 8-bit banding
    // across them is the first thing that gives the image away.
    col += (hash12(gl_FragCoord.xy + uTime) - 0.5) / 255.0;

    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
  `;
}

// Sparks. Trajectory is analytic in the vertex shader from a birth time - no
// per-frame integration, no state to march forward.
const sparkVertexShader = `
  precision highp float;

  attribute vec4 aBirth;      // xy position, z time, w lifetime
  attribute vec3 aSeed;       // x lateral seed, y size px, z birth temperature

  uniform float uTime;
  uniform float uUpdraft;
  uniform float uDprScale;
  uniform float uPointSizeMax;
  uniform float uAspect;

  varying float vTemp;
  varying float vSafe;

  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeZoneAttenuation(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    float age = uTime - aBirth.z;
    if (age < 0.0 || age > aBirth.w) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      vTemp = 0.0; vSafe = 1.0;
      return;
    }

    // Rise: an exponential approach to a terminal speed, which is what drag
    // actually produces - fast off the bed, then settling into a drift. The
    // closed form is the integral of that, so there is nothing to step.
    float tau = ${SPARK_RISE_TAU.toFixed(3)};
    float v = ${SPARK_RISE.toFixed(3)} * uUpdraft;
    float y = aBirth.y + v * (age - tau * (1.0 - exp(-age / tau)));

    // Lateral drift - two incommensurable waves in the height, which reads as
    // a slow curl without the cost of one.
    float s = aSeed.x * 43.7;
    float x = aBirth.x + ${SPARK_DRIFT.toFixed(4)} * uUpdraft * (
        sin(y * 5.3 + s) * 0.6 + sin(y * 2.1 - s * 1.7 + uTime * 0.25) * 0.4);

    // Sparks COOL as they rise, and this is what gives them life: yellow at
    // the bed, red halfway up, out before the top. A spark that kept its
    // colour would read as a floating dot.
    float f = age / aBirth.w;
    vTemp = aSeed.z * exp(-age / ${SPARK_COOL_TAU.toFixed(3)}) * (1.0 - f * f);

    vec2 ndc = vec2((x - 0.5) * 2.0, (y - 0.5) * 2.0);
    gl_Position = vec4(ndc, 0.0, 1.0);
    gl_PointSize = clamp(aSeed.y * uDprScale, 1.0, uPointSizeMax);
    vSafe = safeZoneAttenuation(ndc);
  }
`;

const sparkFragmentShader = `
  precision mediump float;
  varying float vTemp;
  varying float vSafe;
  ${BLACKBODY_CHUNK}
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float r = length(c) * 2.0;
    if (r > 1.0) discard;
    float fall = 1.0 - smoothstep(0.0, 1.0, r);
    vec3 col = blackbody(clamp(vTemp, 0.0, 1.0)) * pow(clamp(vTemp, 0.0, 1.0), 1.4) * 1.7;
    gl_FragColor = vec4(col * fall * vSafe, 1.0);
  }
`;

// Hot-air refraction. The layer that decides whether this reads as heat.
function warpFragmentShader(opts) {
  return `
  precision mediump float;

  uniform sampler2D uScene;
  uniform sampler2D uSim;
  uniform vec2 uResolution;
  uniform float uTime;
  uniform float uAmount;
  uniform float uEnabled;
  varying vec2 vUv;

  ${NOISE_CHUNK}

  void main() {
    if (uEnabled < 0.5) { gl_FragColor = texture2D(uScene, vUv); return; }

    // How hot it is BELOW this pixel - rising air carries the heat of what it
    // came off, so the column above a bright ember is the part that shimmers.
    float src = clamp(vUv.y / ${BED_TOP.toFixed(3)}, 0.0, 1.0);
    float heat = texture2D(uSim, vec2(vUv.x, min(src, 1.0))).r;

    // Falls off with height: the plume loses its coherence as it rises, and a
    // constant-amplitude warp all the way up the frame reads as a glass pane.
    // Steep (6.5, not 3.4): measured against a frozen simulation, the gentler
    // falloff still displaced the top third of the frame enough to resample
    // the background's own dither, which shows up as a faint shimmer in what
    // should be still air.
    float fade = exp(-max(vUv.y - ${BED_TOP.toFixed(3)} * 0.5, 0.0) * 6.5);
    float amp = uAmount * clamp(heat, 0.0, 1.5) * fade;

    // The field SCROLLS UPWARD. This is the single most important detail in
    // the pass: a stationary noise field looks like dirty glass, a rising one
    // looks like air off a fire.
    float ty = vUv.y - uTime * ${WARP_SCROLL.toFixed(3)};
    ${opts.full ? `
    vec2 q = vec2(vUv.x * 8.0, ty * 5.0);
    float nx = fbm(q) - 0.5;
    float ny = fbm(q + 31.7) - 0.5;
    ` : `
    vec2 q = vec2(vUv.x * 7.0, ty * 4.5);
    float nx = vnoise(q) - 0.5;
    float ny = vnoise(q + 19.1) - 0.5;
    `}

    vec2 offset = vec2(nx, ny * 0.65) * amp / uResolution;
    gl_FragColor = texture2D(uScene, vUv + offset);
  }
  `;
}

export class EmberVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];

    this._debugTemp = urlParam('embertemp') === '1';
    this._debugFuel = urlParam('emberfuel') === '1';
    this._forceHeat = urlParam('emberheat') === '1';
    this._warpOff = urlParam('emberwarp') === '0';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, 1);
    camera.lookAt(0, 0, 0);

    this._time = 0;
    this._simAccum = 0;
    this._paused = false;
    this._seeded = false;
    this._lastLow = 0;
    this._synthLow = 0;
    this._synthNext = 1.2;
    this._revive = new THREE.Vector3(0.5, 0.5, 0);
    this._sparkCursor = 0;
    this._sparkDebt = 0;
    this._lastProbeMs = -1e9;
    this._probeData = new Uint8Array(PROBE_W * PROBE_H * 4);
    this._probeCdf = new Float32Array(PROBE_W * PROBE_H);
    this._probeTotal = 0;
    this._lastPaletteRef = undefined;
    this._sizeScratch = new THREE.Vector2();

    const L = this._layout;
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);

    // The state pair: R = temperature, G = fuel. FloatType, which is the whole
    // reason this visualizer declares requiresFloatTextures - an 8-bit state
    // grid would quantise the cooling curve into visible steps within seconds.
    this._sim = new FeedbackBuffer({
      width: L.simW, height: L.simH,
      type: THREE.FloatType, filter: THREE.LinearFilter
    });
    // The scene target, at half resolution: the refraction pass is sampling it
    // with a dependent read and a few pixels of offset, so its own sharpness is
    // not what the result depends on.
    this._sceneRT = new FeedbackBuffer({ scale: 0.75, single: true });
    this._probeRT = new THREE.WebGLRenderTarget(PROBE_W, PROBE_H, {
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      depthBuffer: false, stencilBuffer: false
    });

    this._buildSim();
    this._buildOffscreen();
    this._buildDisplay();
  }

  _buildSim() {
    const L = this._layout;
    this._simUniforms = {
      uPrev: { value: null },
      uTexel: { value: new THREE.Vector2(1 / L.simW, 1 / L.simH) },
      uDt: { value: SIM_DT },
      uBurnRate: { value: BURN_RATE },
      uHeatPerFuel: { value: HEAT_PER_FUEL },
      uDiffusion: { value: DIFFUSION },
      uCooling: { value: COOLING_MAX },
      uFuelRegen: { value: FUEL_REGEN },
      uRevive: { value: this._revive },
      uReviveRadius: { value: REVIVE_RADIUS },
      uTime: { value: 0 }
    };
    this._simMaterial = new THREE.ShaderMaterial({
      uniforms: this._simUniforms,
      vertexShader: simVertexShader, fragmentShader: simFragmentShader,
      depthTest: false, depthWrite: false
    });
    this._probeUniforms = { uSim: { value: null } };
    this._probeMaterial = new THREE.ShaderMaterial({
      uniforms: this._probeUniforms,
      vertexShader: simVertexShader, fragmentShader: probeFragmentShader,
      depthTest: false, depthWrite: false
    });

    this._passGeo = new THREE.PlaneGeometry(1, 1);
    this._passQuad = new THREE.Mesh(this._passGeo, this._simMaterial);
    this._passQuad.frustumCulled = false;
    this._passScene = new THREE.Scene();
    this._passScene.add(this._passQuad);
  }

  _buildOffscreen() {
    const L = this._layout;
    this._offScene = new THREE.Scene();

    this._sceneUniforms = {
      uSim: { value: null },
      uTint: { value: new THREE.Color(0x2a1408) },
      uTime: { value: 0 },
      uAspect: { value: 1.78 },
      uDebug: { value: this._debugTemp ? 1 : (this._debugFuel ? 2 : 0) },
      uTempScale: { value: 0.52 },
      uTempGamma: { value: 2.4 }
    };
    this._sceneMaterial = new THREE.ShaderMaterial({
      uniforms: this._sceneUniforms,
      vertexShader: fullscreenVertexShader,
      fragmentShader: sceneFragmentShader({ cracks: L.cracks, ash: L.ash }),
      depthTest: false, depthWrite: false
    });
    this._bedGeo = new THREE.PlaneGeometry(1, 1);
    this._bedQuad = new THREE.Mesh(this._bedGeo, this._sceneMaterial);
    this._bedQuad.frustumCulled = false;
    this._bedQuad.renderOrder = 0;
    this._offScene.add(this._bedQuad);

    // Sparks
    const n = L.sparks;
    this._sparkCount = n;
    const birth = new Float32Array(n * 4);
    const seed = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      birth[i * 4 + 3] = 1;          // a lifetime, so nothing is born at t=0
      birth[i * 4 + 2] = -1e9;       // already dead
      seed[i * 3 + 1] = lerp(SPARK_PX_MIN, SPARK_PX_MAX, Math.random());
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geo.setAttribute('aBirth', new THREE.BufferAttribute(birth, 4).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 3).setUsage(THREE.DynamicDrawUsage));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 10);
    this._sparkGeo = geo;

    this._sparkUniforms = Object.assign({
      uTime: { value: 0 },
      uUpdraft: { value: 1 },
      uDprScale: { value: 1 },
      uPointSizeMax: { value: 8 },
      uAspect: { value: 1.78 }
    }, {
      uSafeAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    });
    this._sparkMaterial = new THREE.ShaderMaterial({
      uniforms: this._sparkUniforms,
      vertexShader: sparkVertexShader, fragmentShader: sparkFragmentShader,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._sparks = new THREE.Points(geo, this._sparkMaterial);
    this._sparks.frustumCulled = false;
    this._sparks.renderOrder = 1;
    this._offScene.add(this._sparks);
  }

  _buildDisplay() {
    this._warpUniforms = {
      uScene: { value: null },
      uSim: { value: null },
      uResolution: { value: new THREE.Vector2(1920, 1080) },
      uTime: { value: 0 },
      uAmount: { value: WARP_PX_MAX },
      uEnabled: { value: this._warpOff ? 0 : 1 }
    };
    this._warpMaterial = new THREE.ShaderMaterial({
      uniforms: this._warpUniforms,
      vertexShader: fullscreenVertexShader,
      fragmentShader: warpFragmentShader({ full: this._layout.warp === 'full' }),
      depthTest: false, depthWrite: false
    });
    this._displayGeo = new THREE.PlaneGeometry(1, 1);
    this._display = new THREE.Mesh(this._displayGeo, this._warpMaterial);
    this._display.frustumCulled = false;
    this._display.renderOrder = -500;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._renderAll(renderer); };
    this._scene.add(this._display);
  }

  // fbm fuel, so the bed has rich patches and lean ones from the start. Those
  // islands are what make it burn unevenly and for a long time - a uniform fuel
  // field burns as one sheet and is over at once.
  _seed(renderer) {
    const L = this._layout;
    const data = new Float32Array(L.simW * L.simH * 4);
    const h = (x, y) => {
      const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
      return s - Math.floor(s);
    };
    const vn = (x, y) => {
      const xi = Math.floor(x), yi = Math.floor(y);
      let fx = x - xi, fy = y - yi;
      fx = fx * fx * (3 - 2 * fx); fy = fy * fy * (3 - 2 * fy);
      return lerp(lerp(h(xi, yi), h(xi + 1, yi), fx), lerp(h(xi, yi + 1), h(xi + 1, yi + 1), fx), fy);
    };
    const fbm2 = (x, y) => {
      let v = 0, a = 0.5, px = x, py = y;
      for (let i = 0; i < 4; i++) { v += a * vn(px, py); px *= 2.03; py *= 2.03; a *= 0.5; }
      return v;
    };
    for (let y = 0; y < L.simH; y++) {
      for (let x = 0; x < L.simW; x++) {
        const u = x / L.simW, v = y / L.simH;
        const f = fbm2(u * 6.5, v * 3.2);
        const i = (y * L.simW + x) * 4;
        // Hotter and better fuelled low down, where a real bed sits.
        const depth = 1 - v;
        data[i] = Math.max(0, (f - 0.42)) * 1.5 * depth + AMBIENT_SEED;
        data[i + 1] = clamp01(f * 1.35) * (0.35 + 0.65 * depth);
        data[i + 2] = 0;
        data[i + 3] = 1;
      }
    }
    const tex = new THREE.DataTexture(data, L.simW, L.simH, THREE.RGBAFormat, THREE.FloatType);
    tex.needsUpdate = true;
    const copyMat = new THREE.ShaderMaterial({
      uniforms: { uTex: { value: tex } },
      vertexShader: simVertexShader,
      fragmentShader: 'precision highp float; uniform sampler2D uTex; varying vec2 vUv; void main(){ gl_FragColor = texture2D(uTex, vUv); }',
      depthTest: false, depthWrite: false
    });
    const prevMat = this._passQuad.material;
    this._passQuad.material = copyMat;
    for (const rt of [this._sim.read, this._sim.write]) {
      renderer.setRenderTarget(rt);
      renderer.render(this._passScene, this._ortho);
    }
    this._passQuad.material = prevMat;
    copyMat.dispose();
    tex.dispose();
    this._seeded = true;
  }

  _renderAll(renderer) {
    const prevTarget = renderer.getRenderTarget();

    // ensureSize returns true when it REBUILT, which zeroes the state pair -
    // the bed has to be re-seeded then, or the fire silently goes out.
    const simRebuilt = this._sim.ensureSize(renderer);
    this._sceneRT.ensureSize(renderer);
    if (simRebuilt || !this._seeded) this._seed(renderer);

    // --- 1. combustion, fixed timestep -------------------------------------
    if (!this._paused) {
      let steps = 0;
      while (this._simAccum >= SIM_DT && steps < SIM_MAX_SUBSTEPS) {
        this._passQuad.material = this._simMaterial;
        this._simUniforms.uPrev.value = this._sim.read.texture;
        this._simUniforms.uTime.value = this._time;
        renderer.setRenderTarget(this._sim.write);
        renderer.render(this._passScene, this._ortho);
        this._sim.swap();
        // A revive is ONE substep's worth of injection, not a level held
        // across frames - otherwise a sustained bass line would pour fuel in
        // forever and the bed would run away.
        this._revive.z = 0;
        this._simAccum -= SIM_DT;
        steps++;
      }
      // Whatever could not be stepped is dropped rather than carried: a
      // backlog is how a fixed-step loop spirals after one slow frame.
      if (this._simAccum > SIM_DT) this._simAccum = 0;
    }

    const simTex = this._sim.read.texture;
    this._sceneUniforms.uSim.value = simTex;
    this._warpUniforms.uSim.value = simTex;

    // --- 2. the scene, offscreen -------------------------------------------
    renderer.setRenderTarget(this._sceneRT.read);
    renderer.render(this._offScene, this._ortho);

    // --- 3. heat probe, for where sparks are born --------------------------
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    if (!this._paused && nowMs - this._lastProbeMs > PROBE_EVERY_MS) {
      this._lastProbeMs = nowMs;
      this._passQuad.material = this._probeMaterial;
      this._probeUniforms.uSim.value = simTex;
      renderer.setRenderTarget(this._probeRT);
      renderer.render(this._passScene, this._ortho);
      try {
        renderer.readRenderTargetPixels(this._probeRT, 0, 0, PROBE_W, PROBE_H, this._probeData);
        this._buildCdf();
      } catch (e) { /* readback unsupported - sparks fall back to uniform */ }
    }

    renderer.setRenderTarget(prevTarget);
    this._warpUniforms.uScene.value = this._sceneRT.read.texture;
    renderer.getDrawingBufferSize(this._sizeScratch);
    this._warpUniforms.uResolution.value.copy(this._sizeScratch);
    this._sparkUniforms.uPointSizeMax.value = Math.max(1, SPARK_PX_MAX * 3);
  }

  // Cumulative distribution over the probe grid, so a spark's birthplace is
  // drawn in proportion to how hot that cell is. Sparks come off the hottest
  // parts of the bed, not off it uniformly.
  _buildCdf() {
    const d = this._probeData, cdf = this._probeCdf;
    let acc = 0;
    for (let i = 0; i < PROBE_W * PROBE_H; i++) {
      const v = d[i * 4] / 255;
      acc += v * v;               // squared, so the hot spots dominate properly
      cdf[i] = acc;
    }
    this._probeTotal = acc;
  }

  _pickBirth() {
    if (this._probeTotal <= 1e-6) return null;
    const r = Math.random() * this._probeTotal;
    const cdf = this._probeCdf;
    let lo = 0, hi = PROBE_W * PROBE_H - 1;
    while (lo < hi) { const m = (lo + hi) >> 1; if (cdf[m] < r) lo = m + 1; else hi = m; }
    const cx = lo % PROBE_W, cy = (lo / PROBE_W) | 0;
    return {
      x: (cx + Math.random()) / PROBE_W,
      // The probe covers the BED, which is the bottom BED_TOP of the frame.
      y: ((cy + Math.random()) / PROBE_H) * BED_TOP,
      t: Math.min(1, this._probeData[lo * 4] / 255 * 2)
    };
  }

  _emitSparks(count) {
    const birth = this._sparkGeo.attributes.aBirth;
    const seed = this._sparkGeo.attributes.aSeed;
    let emitted = 0;
    for (let k = 0; k < count; k++) {
      const b = this._pickBirth();
      if (!b) break;
      const i = this._sparkCursor;
      this._sparkCursor = (this._sparkCursor + 1) % this._sparkCount;
      birth.array[i * 4] = b.x;
      birth.array[i * 4 + 1] = b.y;
      birth.array[i * 4 + 2] = this._time;
      birth.array[i * 4 + 3] = lerp(SPARK_LIFE_MIN, SPARK_LIFE_MAX, Math.random());
      seed.array[i * 3] = Math.random();
      seed.array[i * 3 + 1] = lerp(SPARK_PX_MIN, SPARK_PX_MAX, Math.random());
      seed.array[i * 3 + 2] = Math.max(0.45, b.t);
      emitted++;
    }
    if (emitted) { birth.needsUpdate = true; seed.needsUpdate = true; }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (paused) return;   // gel de la simulation et de l'accumulateur

    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._simAccum += dt;

    const director = profile.director;
    const bands = director && director.bands;
    const character = director && director.character;
    // Without a radiopi analysis director.bands is all zeros, and the revive
    // below only fires on a RISE - so on an unanalysed track the fire would
    // never once be poked and the bed would sit at its regen equilibrium with
    // no hot spots at all. The fallback therefore has to STEP, not drift: a
    // slow sine of any amplitude rises a few thousandths per frame and never
    // crosses the threshold. Same shape as the real signal, which arrives in
    // discrete 100ms buckets.
    const has = !!(bands && director.hasReliableGrid);
    let low, mid, high;
    if (has) {
      low = clamp01(bands.low); mid = clamp01(bands.mid); high = clamp01(bands.high);
    } else {
      this._synthLow = Math.max(0, this._synthLow - dt * 1.7);
      this._synthNext -= dt;
      if (this._synthNext <= 0) {
        // Irregular on purpose - a fire is poked at no particular tempo.
        this._synthNext = lerp(1.6, 5.2, Math.random());
        this._synthLow = lerp(0.55, 1.0, Math.random());
      }
      low = this._synthLow;
      mid = 0.45 + 0.3 * Math.sin(this._time * 0.23 + 1.1);
      high = 0.4 + 0.3 * Math.sin(this._time * 0.41 + 2.2);
    }
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;

    // bands.low -> someone pokes the fire. Irregular by nature, which is the
    // point: this is the main gesture and it must never feel metronomic.
    const rose = low - this._lastLow > REVIVE_RISE;
    this._lastLow = low;
    if (rose || this._forceHeat) {
      const b = this._pickBirth();
      const amp = lerp(0.6, 1.35, dynamics);
      this._revive.set(
        b ? b.x : Math.random(),
        b ? clamp01(b.y / BED_TOP) : Math.random() * 0.7,
        REVIVE_HEAT * amp
      );
      this._simUniforms.uRevive.value = this._revive;
    }

    // progress -> cooling. Weakest mid-track, so the fire is at its liveliest
    // in the heart of the piece and settles at both ends.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const arc = Math.sin(Math.PI * progress);
    this._simUniforms.uCooling.value = lerp(COOLING_MAX, COOLING_MIN, arc);
    this._simUniforms.uFuelRegen.value = FUEL_REGEN * lerp(0.7, 1.3, arc);

    // bands.mid -> the updraft: how fast sparks climb and how much the air bends.
    const updraft = lerp(UPDRAFT_MIN, UPDRAFT_MAX, mid);
    this._sparkUniforms.uUpdraft.value = updraft;
    this._warpUniforms.uAmount.value = this._warpOff ? 0 : WARP_PX_MAX * lerp(0.6, 1.0, mid);

    // bands.high -> how many sparks are born.
    this._sparkDebt += lerp(BIRTH_RATE_MIN, BIRTH_RATE_MAX, high) * dt;
    if (this._sparkDebt >= 1) {
      const n = Math.min(this._sparkDebt | 0, 48);
      this._emitSparks(n);
      this._sparkDebt -= n;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this._sparkUniforms.uTime.value = this._time;
    this._sparkUniforms.uDprScale.value = dpr;
    this._sceneUniforms.uTime.value = this._time;
    this._warpUniforms.uTime.value = this._time;

    const aspect = this._camera && this._camera.aspect ? this._camera.aspect : 1.78;
    this._sceneUniforms.uAspect.value = aspect;
    this._sparkUniforms.uAspect.value = aspect;

    // palette -> the BACKGROUND only, and faintly. The fire's colour is
    // physical; it never takes the cover's palette. On a blue cover the embers
    // stay orange - see this file's header.
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      const dom = pal && pal.dominantColors && pal.dominantColors.length ? pal.dominantColors[0] : 0x3a2418;
      this._sceneUniforms.uTint.value.set(dom).lerp(new THREE.Color(0x2a1408), 0.72);
    }
  }

  dispose() {
    this._scene.remove(this._display);
    this._displayGeo.dispose();
    this._warpMaterial.dispose();
    this._bedGeo.dispose();
    this._sceneMaterial.dispose();
    this._sparkGeo.dispose();
    this._sparkMaterial.dispose();
    this._passGeo.dispose();
    this._simMaterial.dispose();
    this._probeMaterial.dispose();
    this._sim.dispose();
    this._sceneRT.dispose();
    this._probeRT.dispose();
  }
}
